/* eslint-disable max-lines */
/* eslint-disable camelcase */

import { eventKeyCodes } from './helpers/eventKeyCodes';
import { pushToDataLayer } from './analytics/pushToDataLayer';
import { activateProductSelector, handleProductSelectorContinueClick } from './analytics/listeners/productSelectorEvents'

/**
 * Represents a ProductSelectorHandler class that handles the functionality
 * for product selection and question selection.
 */
export class ProductSelectorHandler {
  /**
   * @returns {boolean}
   */
  static isOnPage() {
    return $('.js-product-selector').length > 0;
  }

  constructor() {
    this.currentStepId = '0';
    this.userSegment = null;
    this.stepHistory = [];
    this.availableProducts = [];
    this.init();
  }

  init() {
    const $selectorBtn = $('.js-product-selector-btn');
    $selectorBtn.css({
      'opacity': 0.4,
      'pointer-events': 'none'
    }).attr('aria-disabled', 'true');

    this.fetchData()
      .done(data => {
        this.data = data;
        this.availableProducts = [...data.productSelectorAllProducts];

        const returnToProductSelectorFlag = sessionStorage.getItem('returningToProductSelector');
        const returnToLastStepFlag = sessionStorage.getItem('returningToProductSelectorLastStep');

        if (returnToProductSelectorFlag === 'true') {
          this.scrollToProductSelector();

          sessionStorage.removeItem('returningToProductSelector');
        }

        if (returnToLastStepFlag === 'true') {
          this.handleReturnState();
        }

        this.addBFcacheListener();
        this.activateSelector();
        this.buttonsData();
        this.addNavigationEvents();
        this.addKeydownEvents();
        this.complementaryButtonClick();

        $selectorBtn.css({
          'opacity': 1,
          'pointer-events': 'unset'
        }).attr('aria-disabled', 'false');
      })
  }

  fetchData() {
    const componentId = $('.js-product-selector').data('component-id') || '';

    return $.ajax({
      url: `/products/productselectordetails?segment=men&segmentDirectory=/men&componentId=${componentId}`,
      method: 'GET',
      dataType: 'json',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  /**
   * Activates the product selector by showing the question container
   * when the product selector button is clicked.
   */
  activateSelector() {
    $(document).on('click', '.js-product-selector-btn', () => {
      this.currentStepId = '0';
      this.availableProducts = [...this.data.productSelectorAllProducts];
      activateProductSelector();
      sessionStorage.removeItem('productSelectorReturnState');

      $('.js-question-container').css('display', 'flex');
      $('.js-product-selector-btn').hide();
      $('.product-selector-text').attr('aria-hidden', 'true');
      this.renderQuestion(this.data);
    });
  }

  /**
   * Updates the width of the progress bar based on the current step and total steps.
   *
   * @param {number} currentStepId - The current step id in the process.
   * @param {number} totalSteps - The total number of steps in the process.
   */
  progressBar(currentStepId, totalSteps) {
    const $progressBarLine = $('.question-progress-bar-line');
    const progressPercentage = (currentStepId / totalSteps) * 100;

    $progressBarLine.css('width', progressPercentage + '%');
  }

  /**
   * * Sets the text for the back and next buttons based on the current data.
   */
  buttonsData() {
    $('.js-question-back-btn').text(this.data.backButtonText);
    $('.js-question-next-btn').text(this.data.continueButtonText);
  }

  /**
   * Centralized method to handle all logic for proceeding to the next step.
   * It updates user segment, history, filters products, and determines the next action
   * (render next question or redirect).
   *
   * @param {object} answer - The selected answer object.
   * @param {object} currentStepData - The data for the current step.
   */
  proceedToNextStep(answer, currentStepData) {
    const currentStepType = currentStepData.type;
    const selectedFilterKey = answer.filterKey;

    handleProductSelectorContinueClick(answer, currentStepData, this.stepHistory.length);

    // Store the selected segment
    if (answer.key === 'cgr') {
      this.userSegment = 'cgr';
    } else if (answer.key === 'women|men') {
      this.userSegment = null;
    } else if (this.userSegment !== 'cgr') {
      if (answer.key === 'female') {
        this.userSegment = 'female';
      } else if (answer.key === 'male') {
        this.userSegment = 'male';
      }
    }

    // Save current step data in history
    this.stepHistory.push({
      stepId: this.currentStepId,
      productsBefore: [...this.availableProducts],
      typeKeyValue: {
        type: currentStepType,
        key: selectedFilterKey
      },
      segment: this.userSegment
    });

    // Get next step and filter available products
    const nextStep = this.getNextStepId(this.currentStepId);
    this.getFilteredProducts(this.currentStepId, answer.key);

    if (nextStep && this.availableProducts.length > 1) {
      this.currentStepId = nextStep;
      this.renderQuestion(this.data);
      $('.js-question-container').attr('tabindex', -1).focus();
    } else {
      $('.question-header, .question-body, .question-footer').hide();
      $('.product-selector-loading-wrapper').css('display', 'flex').focus();

      setTimeout(() => {
        this.redirectToFilterPage();
      }, 500);
    }
  }

  /**
   *  Handles all logic for navigating to a previous step in the product selector.
   */
  handleBackNavigation() {
    const $questionContainer = $('.js-question-container');

    while (this.stepHistory.length > 0) {
      const previousState = this.stepHistory.pop();
      this.currentStepId = previousState.stepId;
      this.availableProducts = [...previousState.productsBefore];

      const stepDataForCheck = this.data.productSelectorSteps.find(step => step.stepId === this.currentStepId);

      const validAnswers = stepDataForCheck.answers.filter(answer =>
        this.countProductsForAnswer(stepDataForCheck, answer.key) > 0
      );

      // If this step is a non-single-answer step, render it and stop the loop.
      if (validAnswers.length !== 1) {
        this.renderQuestion(this.data);
        $questionContainer.attr('tabindex', -1).focus();

        return;
      }
    }

    $questionContainer.css('display', 'none');
    $('.js-product-selector-btn').show();
  }

  /**
   * Adds click event listeners to the back and next buttons for navigating through steps.
   */
  addNavigationEvents() {
    const $backButton = $('.js-question-back-btn');
    const $nextButton = $('.js-question-next-btn');

    $backButton.on('click', () => {
      this.handleBackNavigation();
    });

    $nextButton.on('click', () => {
      const $selected = $('.js-question-radio-btn:checked');

      if ($selected.length) {
        const selectedAnswerKeyValue = $selected.val();
        const currentStepData = this.data.productSelectorSteps.find(step => step.stepId === this.currentStepId);
        const answer = currentStepData.answers.find(ans => ans.key === selectedAnswerKeyValue);

        this.proceedToNextStep(answer, currentStepData);
      }
    });
  }

  /**
   * @returns {string|null} The suffix string corresponding to the user segment, or `null` if no match is found.
   */
  getStepSuffix() {
    if (this.userSegment === 'cgr') {
      return 'cgr';
    } else if (this.userSegment === 'female') {
      return 'women';
    } else if (this.userSegment === 'male') {
      return 'men';
    }

    return null;
  }

  /**
   * Gets the ID of the next step based on the current step.
   *
   * @param {string} currentStepId - The current step id in the process.
   * @param {string} selectedAnswerKey - The Key of the selected answer.
   * @returns {string | null} The StepId of the next step, or null if it's the end or undefined.
   */
  getNextStepId(currentStepId) {
    const steps = this.data.productSelectorSteps.map(step => step.stepId);
    const currentIndex = steps.indexOf(currentStepId);

    if (currentStepId === '0') {
      return steps.includes('1') ? '1' : null;
    }

    const isPlainNumber = stepId => /^\d+$/.test(stepId);

    const stepSuffix = this.getStepSuffix();
    const remainingSteps = steps.slice(currentIndex + 1);

    const nextStepId = remainingSteps.find(step => {
      if (isPlainNumber(step)) { return true };
      if (stepSuffix && step.toLowerCase().endsWith(`-${stepSuffix}`)) { return true };

      return false;
    });

    if (!nextStepId) {
      return null;
    }

    return nextStepId;
  }

  /**
   * Applies filtering logic to a list of products based on step type and answer key.
   * This is a pure function and does not modify any instance properties directly.
   * @param {Array<object>} productsToFilter - The list of products to filter.
   * @param {string} stepType - The type of the current step (e.g., 'segment', 'gender').
   * @param {string} answerKey - The key of the selected answer.
   * @returns {Array<object>} A new array containing the filtered products.
   */
  applyProductFilter(productsToFilter, stepType, answerKey) {
    const lowerCaseAnswerKey = answerKey.toLowerCase();
    let filteredProducts = [...productsToFilter];

    switch (stepType) {
      case 'segment':
        if (lowerCaseAnswerKey === 'cgr') {
          filteredProducts = filteredProducts.filter(product =>
            product.availableForCgr === true ||
            product.productSelectorFamily?.gender === 'Unisex');
        } else if (lowerCaseAnswerKey === 'women|men') {
          filteredProducts = filteredProducts.filter(product =>
            product.availableForMen === true ||
            product.availableForWomenAcc === true);
        }
        break;
      case 'gender':
        if (lowerCaseAnswerKey === 'female') {
          filteredProducts = filteredProducts.filter(product =>
            product.availableForWomenAcc === true ||
            product.productSelectorFamily?.gender === 'Female' ||
            product.productSelectorFamily?.gender === 'Unisex');
        } else if (lowerCaseAnswerKey === 'male') {
          filteredProducts = filteredProducts.filter(product =>
            product.availableForMen === true ||
            product.productSelectorFamily?.gender === 'Male' ||
            product.productSelectorFamily?.gender === 'Unisex');
        }
        break;
      case 'levelofmobility':
        filteredProducts = filteredProducts.filter(product => {
          const family = product.productSelectorFamily;
          if (!family) { return false; }
          let matches = false;
          if (lowerCaseAnswerKey === 'fullmobility' && (family.fullMobility > 0)) { matches = true; }
          if (lowerCaseAnswerKey === 'needassistance' && (family.needAssistance > 0)) { matches = true; }
          if (lowerCaseAnswerKey === 'bedridden' && (family.bedridden > 0)) { matches = true; }

          return matches;
        });
        break;
      case 'absorbency':
        filteredProducts = filteredProducts.filter(product => {
          if (!product.levelOfIncontinence || product.levelOfIncontinence.length === 0) {
            return false;
          }

          return product.levelOfIncontinence.some(level => level.toLowerCase() === lowerCaseAnswerKey);
        });
        break;
      case 'usageoccasion':
        if (lowerCaseAnswerKey === 'night time') {
          filteredProducts = filteredProducts.filter(product =>
            product.usageOccasion && product.usageOccasion.includes('Night time')
          );
        }
        break;
      case 'type':
        filteredProducts = filteredProducts.filter(product => {
          if (product.productSelectorFamily && product.productSelectorFamily.key) {
            const answerKeys = lowerCaseAnswerKey
              .split('|')
              .map(key => key.trim().toLowerCase());

            return answerKeys.includes(product.productSelectorFamily.key.toLowerCase());
          }

          return false;
        });
        break;
      default:
        break;
    }

    return filteredProducts;
  }

  /**
   * Filters the available products based on the current selector step and
   * the user's selected answer key, then updates product list.
   *
   * @param {string|number} currentStepId - The current step id in the process.
   * @param {string} selectedAnswerKey - The Key of the selected answer.
   * @returns {void}
   */
  getFilteredProducts(currentStepId, selectedAnswerKey) {
    const currentStepData = this.data.productSelectorSteps.find(step => step.stepId === currentStepId);

    if (!currentStepData) {
      return;
    }

    this.availableProducts = this.applyProductFilter(
      this.availableProducts,
      currentStepData.type,
      selectedAnswerKey
    );
  }

  /**
   * Counts the number of products that would match if a specific answer is selected for a given step.
   * This method temporarily applies the filter associated with the selected answer to a
   * provided list of products and returns the count of the resulting products.
   * It uses the `applyProductFilter` method internally to perform the filtering logic.
   *
   * @param {object} currentStepData - The data object for the current step
   * @param {object} selectedAnswerKey - The answer object for which to count products.
   * @param {Array<object>} productsToCheck - An array of product objects to be filtered and counted.
   * @returns {number} The number of products from `productsToCheck` that match the filter
   */
  countProductsForAnswer(currentStepData, selectedAnswerKey) {
    const tempFilteredProducts = this.applyProductFilter(
      this.availableProducts,
      currentStepData.type,
      selectedAnswerKey
    );

    return tempFilteredProducts.length;
  }

  /**
   * Adds keydown events.
   * Enables keyboard navigation with Enter and Escape.
   */
  addKeydownEvents() {
    const $popup = $('.js-question-popup');

    $popup.on('keydown', event => {
      if (event.keyCode === eventKeyCodes.enter || event.keyCode === eventKeyCodes.space) {
        $(event.currentTarget).click();
      }
    });
  }

  /**
   * Handles the functionality for selecting an option within the product selector.
   *
   * When a question is clicked, it finds the associated radio button
   * and marks it as checked.
   */
  optionSelection() {
    const $question = $('.js-question');
    const $nextButton = $('.js-question-next-btn');
    $nextButton.css({
      'opacity': 0.4,
      'pointer-events': 'none'
    }).attr('aria-disabled', 'true');

    $question.on('click', event => {
      const $clickedQuestion = $(event.currentTarget);
      const $radioButton = $clickedQuestion.find('.js-question-radio-btn');
      $nextButton.css({
        'opacity': 1,
        'pointer-events': 'unset'
      }).attr('aria-disabled', 'false');

      if ($radioButton.length) {
        $radioButton.prop('checked', true);
      }
    })
  }

  /**
   * Get the total number of product selector steps that apply to the current segment.
   * A step is counted if its `stepId` is a plain number
   * or if it ends with the current suffix.
   *
   * @returns {number} The number of steps matching the current segment.
   */
  getTotalStepsForSegment() {
    const stepSuffix = this.getStepSuffix();

    const filteredSteps = this.data.productSelectorSteps.filter(step => {
      const isPlainNumber = /^\d+$/.test(step.stepId);
      if (isPlainNumber) { return true; }
      if (stepSuffix && step.stepId.toLowerCase().endsWith(`-${stepSuffix}`)) { return true; }

      return false;
    });

    return filteredSteps.length;
  }

  /**
   * Renders the current question based on the data provided and updates the progress bar.
   * Dynamically generates radio buttons with corresponding answers, appends them to the question container,
   * and updates the question's heading, subheading, and additional labels based on the current step.
   *
   * @param {Object} data - The data object containing the product selector steps.
   */
  renderQuestion(data) {
    const $questionBody = $('.js-question-body');
    const currentStepData = data.productSelectorSteps.find(step => step.stepId === this.currentStepId);

    if (currentStepData) {
      const validAnswers = currentStepData.answers.filter(answer =>
        this.countProductsForAnswer(currentStepData, answer.key) > 0
      );

      // If there's only one valid path, take it automatically.
      if (validAnswers.length === 1) {
        const singleAnswer = validAnswers[0];
        this.proceedToNextStep(singleAnswer, currentStepData);

        return;
      }

      const currentStepNumber = this.stepHistory.length + 1;
      const totalSteps = this.getTotalStepsForSegment() + 1;
      this.progressBar(currentStepNumber, totalSteps);

      $('.question-subheading').text(currentStepData.progressBarStepTitle);
      $('.question-heading').text(currentStepData.question);
      $('.question-popup-label').text(currentStepData.complementaryLinkText);
      $('.panel-description').text(currentStepData.complementaryText);

      // Clear any existing question labels before adding new ones
      $questionBody.empty();

      // Dynamically create and append question-radio-btn elements based on the number of valid answers
      validAnswers.forEach((answer, index) => {
        const wrapper = $('<div>', {
          class: 'question js-question'
        });

        // Create the radio button input
        const radioButton = $('<input>', {
          type: 'radio',
          id: `question-${index}`,
          name: 'question',
          class: 'question-radio-btn js-question-radio-btn',
          value: answer.key
        });

        // Create the label for the radio button
        const label = $('<label>', {
          for: `question-${index}`,
          class: 'question-label'
        });

        // Append the radio button to the question wrapper
        const $wrapper = wrapper.append(radioButton);

        // If there's an image for the answer, create and append the image
        if (answer.icon) {
          const img = $('<img>', {
            class: 'question-image lazyload',
            src: `${answer.icon}?w=200&h=10000&imPolicy=dynamic`,
            alt: answer.icon.alt || '',
            width: 50,
            height: 50,
            title: answer.icon.title || ''
          });

          // Append the image to the question wrapper
          $wrapper.append(img);
        }

        // Append the question wrapper and label to the question container
        $questionBody.append($wrapper.append(label.append(answer.value)));
      });

      this.optionSelection();
    }
  }

  /**
   * Redirects the user to the appropriate product filter page based on option selection.
   * Saves the current selection history to sessionStorage.
   */
  redirectToFilterPage() {
    // URLs for redirecting based on user segment
    const menFilterPage = this.data.menProductFilterPage;
    const cgrFilterPage = this.data.cgrProductFilterPage;
    const womenFilterPage = this.data.womenProductFilterPage;
    const originUrl = window.location.origin;
    const pageUrl = window.location.href;

    const segmentPageMap = {
      female: womenFilterPage,
      male: menFilterPage,
      cgr: cgrFilterPage
    };

    const targetPagePath = segmentPageMap[this.userSegment];

    // Check for valid path and redirect to filter page
    if (targetPagePath) {
      // Generate parameters from stepHistory
      const typeKeyParams = this.stepHistory
        .filter(
          step =>
            step.typeKeyValue?.type &&
            step.typeKeyValue?.key &&
            step.typeKeyValue.key !== '-'
        )
        .map(step => {
          const paramType = (this.userSegment === 'cgr' && step.typeKeyValue.type === 'segment')
            ? 'gender'
            : step.typeKeyValue.type;

          const paramKey = step.typeKeyValue.key;

          return `${paramType}=${paramKey}`;
        })
        .join('&');

      const simplifiedHistory = [...this.stepHistory].map(({ stepId, typeKeyValue, segment }) => ({
        stepId,
        typeKeyValue,
        segment
      }));

      const productFormSaveHistory = {
        userSegment: this.userSegment,
        stepHistory: simplifiedHistory,
        availableProducts: this.availableProducts
      };

      let stepIdToSave = this.currentStepId;
      let historyToSave = [...this.stepHistory];
      let productsToSaveForCurrentStep;

      if (historyToSave.length > 0) {
        productsToSaveForCurrentStep = historyToSave[historyToSave.length - 1].productsBefore;
        historyToSave.pop();
      } else {
        productsToSaveForCurrentStep = [...this.data.productSelectorAllProducts];
      }

      const stateToSave = {
        currentStepId: stepIdToSave,
        userSegment: this.userSegment,
        stepHistory: historyToSave,
        availableProducts: productsToSaveForCurrentStep
      };

      sessionStorage.setItem('productSelectorSource', 'true');
      sessionStorage.setItem('productSelectorPageUrl', pageUrl);
      sessionStorage.setItem('productSelectorHistory', JSON.stringify(productFormSaveHistory));
      sessionStorage.setItem('productSelectorReturnState', JSON.stringify(stateToSave));

      window.location.href = `${originUrl}${targetPagePath}?${typeKeyParams}&productSelector=true`;
    }
  }

  /**
   * Handles the restoration of the product selector state from sessionStorage.
   * If a valid saved state is found, it updates the component's properties,
   * re-renders the current question, re-attaches event listeners, and scrolls
   * to the selector. It also cleans up the relevant sessionStorage flags.
   *
   * @returns {boolean} Returns true if the state was successfully restored, otherwise returns false.
   */
  handleReturnState() {
    const savedStateJSON = sessionStorage.getItem('productSelectorReturnState');

    if (savedStateJSON) {
      try {
        const savedState = JSON.parse(savedStateJSON);

        if (savedState.currentStepId && savedState.stepHistory) {
          this.currentStepId = savedState.currentStepId;
          this.userSegment = savedState.userSegment;
          this.stepHistory = savedState.stepHistory;
          this.availableProducts = [...savedState.availableProducts];

          sessionStorage.removeItem('returningToProductSelectorLastStep');
          sessionStorage.removeItem('productSelectorReturnState');

          $('.js-question-container').css('display', 'flex');
          $('.js-product-selector-btn').attr('tabindex', -1);

          const stepDataForCheck = this.data.productSelectorSteps.find(step => step.stepId === this.currentStepId);
          const validAnswers = stepDataForCheck.answers.filter(answer =>
            this.countProductsForAnswer(stepDataForCheck, answer.key) > 0
          );

          if (validAnswers.length === 1) {
            this.handleBackNavigation();
          } else {
            this.renderQuestion(this.data);
          }

          this.scrollToProductSelector();

          return true;
        }
      } catch (e) {
        window.console.error(e);
        sessionStorage.removeItem('returningToProductSelectorLastStep');
        sessionStorage.removeItem('productSelectorReturnState');
      }
    } else {
      sessionStorage.removeItem('returningToProductSelectorLastStep');
      sessionStorage.removeItem('productSelectorReturnState');
    }

    return false;
  }

  /**
   * Scrolls the page to the product selector container.
   * Uses a slight delay to ensure the element is fully rendered and visible.
   */
  scrollToProductSelector() {
    const $selectorElement = $('.js-product-selector');

    if ($selectorElement.length) {
      setTimeout(() => {
        $('html, body').animate({
          scrollTop: $selectorElement.offset().top - 40
        }, 500);
      }, 100);
    }
  }

  /**
   * Handles the data layer push when a user click on complementary button.
   */
  complementaryButtonClick() {
    $('.js-question-popup').off('click').on('click', () => {
      const currentStepId = this.currentStepId;
      const sequentialStepNumber = this.stepHistory.length + 1;

      const currentStepData = this.data.productSelectorSteps.find(
        step => step.stepId === currentStepId
      );

      const $selected = $('.js-question-radio-btn:checked');
      let selectedAnswerValueOnClick = 'N/A';

      if ($selected.length) {
        const selectedIndex = $('.js-question-radio-btn').index($selected);
        selectedAnswerValueOnClick = currentStepData.answers[selectedIndex].value;
      }

      pushToDataLayer({
        event: 'ga4_product_selector',
        ga4_data: {
          action_type: 'Complementary Link Click',
          step_number: `Step ${sequentialStepNumber}`,
          step_name: currentStepData.progressBarStepTitle,
          answer: selectedAnswerValueOnClick
        }
      });
    });
  }

  /**
   * Resets the product selector to its initial state.
   * Clears history, resets steps, and updates the UI.
   */
  resetToInitialState() {
    this.currentStepId = '0';
    this.userSegment = null;
    this.stepHistory = [];
    this.availableProducts = [...this.data.productSelectorAllProducts];

    // Reset the UI to its initial state
    $('.js-question-container').css('display', 'none');
    $('.js-product-selector-btn').show();
    $('.product-selector-text').attr('aria-hidden', 'false');
    $('.product-selector-loading-wrapper').css('display', 'none');
    $('.question-header, .question-body, .question-footer').show();

    // Clear any session storage items related to the selector's state
    sessionStorage.removeItem('productSelectorReturnState');
    sessionStorage.removeItem('returningToProductSelectorLastStep');
    sessionStorage.removeItem('productSelectorHistory');
    sessionStorage.removeItem('productSelectorSource');
    sessionStorage.removeItem('productSelectorPageUrl');

    // Ensure the product selector button is enabled and fully visible
    const $selectorBtn = $('.js-product-selector-btn');
    $selectorBtn.css({
      'opacity': 1,
      'pointer-events': 'unset'
    }).attr('aria-disabled', 'false');
  }

  /**
   * Adds the pageshow event listener to handle BFCache restoration.
   */
  addBFcacheListener() {
    window.addEventListener('pageshow', event => {
      // event.persisted is true if page is loaded from bfcache
      if (event.persisted) {
        this.resetToInitialState();
      }
    });
  }
}
